/* eslint-disable camelcase */
import axios from 'axios';
import type { AxiosError } from 'axios';

import type { Collection as CollectionDto } from './dto';
import { mapCollectionDtoToModel } from './mapper';
import type { Collection as CollectionModel } from './model';

type SiteId = string;
type CollectionId = string;
type CollectionName = string;

const API_ROOT = `/api/v1/sites`;

export class ExistingCollectionError extends Error {}

export const COLLECTIONS_LIST_QUERY_KEY = 'collections';

function createEndpointPath(siteId: SiteId, collectionId?: CollectionId): string {
  if (typeof collectionId === 'undefined') {
    return `${API_ROOT}/${siteId}/collections`;
  }

  return `${API_ROOT}/${siteId}/collections/${collectionId}`;
}

export async function getAll(siteId: SiteId): Promise<Array<CollectionModel>> {
  const path = createEndpointPath(siteId);
  const response = await axios.get<{ items: Array<CollectionDto>; success: boolean }>(path);

  return response.data.items.map(mapCollectionDtoToModel);
}

export async function rename(
  siteId: SiteId,
  collectionId: CollectionId,
  name: CollectionName,
): Promise<void> {
  const path = createEndpointPath(siteId, collectionId);

  try {
    await axios.patch(path, { name });
  } catch (err) {
    if ((err as AxiosError)?.response?.status === 400) {
      throw new ExistingCollectionError();
    }

    throw err;
  }
}

export async function remove(siteId: SiteId, collectionId: CollectionId): Promise<void> {
  const path = createEndpointPath(siteId, collectionId);

  await axios.delete(path);
}

export async function create(siteId: SiteId, name: CollectionName): Promise<CollectionId> {
  const path = createEndpointPath(siteId);

  try {
    // eslint-disable-next-line camelcase
    const response = await axios.post<{ collection_id: number }>(path, { name });

    return String(response.data.collection_id);
  } catch (err) {
    if ((err as AxiosError)?.response?.status === 400) {
      throw new ExistingCollectionError();
    }

    throw err;
  }
}

type HighlightId = string;

export async function addHighlightToCollection(
  siteId: SiteId,
  collectionId: CollectionId,
  highlightId: HighlightId,
): Promise<void> {
  const path = `${createEndpointPath(siteId, collectionId)}/highlights`;

  await axios.post(path, { highlight_ids: [highlightId] });
}

export async function removeHighlightFromCollection(
  siteId: SiteId,
  collectionId: CollectionId,
  highlightId: HighlightId,
): Promise<void> {
  const path = `${createEndpointPath(siteId, collectionId)}/highlights/${highlightId}`;

  await axios.delete(path);
}

export async function trackCollection(siteId: SiteId, collectionId: CollectionId): Promise<void> {
  const path = `${createEndpointPath(siteId)}/events`;
  const id = Number.parseInt(collectionId, 10);

  if (id) {
    axios.post(path, {
      collection_id: id,
      event: 'collection-viewed',
    });
  }
}

export async function trackCollectionShared(
  siteId: SiteId,
  collectionId: CollectionId,
): Promise<void> {
  const path = `${createEndpointPath(siteId)}/events`;
  const id = Number.parseInt(collectionId, 10);

  if (id) {
    axios.post(path, {
      collection_id: id,
      event: 'collection-shared',
    });
  }
}
